/**
 * Sentinel `displayEn` value meaning "the platform serif stack, no webfont".
 *
 * Not a cosmetic shortcut: several EasyOrders families compute their editorial
 * headings to the generic `ui-serif` themselves, so requesting a Google serif
 * would be *less* faithful than reproducing the same system stack. It also
 * costs no network request. The buyer's font loader skips it and
 * `StorefrontThemeStyle` expands it to the serif stack.
 */
export const SYSTEM_SERIF_FONT = "System Serif";
/** The stack `SYSTEM_SERIF_FONT` expands to (matches the EO computed value). */
export const SYSTEM_SERIF_STACK = 'ui-serif, Georgia, Cambria, "Times New Roman", serif';
export const LATIN_FONT_OPTIONS = [
    {
        value: "Inter",
        label: { ar: "Inter (مقترح)", en: "Inter (recommended)" },
        weights: "300;400;500;600;700;800",
    },
    {
        // Serif. Used by the `hero-eyebrow` featured-products banner title
        // (dark, volta) — measured Newsreader 28px w400 on their banner.
        value: "Newsreader",
        label: { ar: "Newsreader", en: "Newsreader" },
        weights: "300;400;500;600;700",
    },
    {
        // The restaurant storefront families. Each is the face its artboard is
        // drawn in, and each carries the FULL axis that design uses — STACK sets
        // 900 throughout, so requesting up to 700 would render its whole masthead
        // and every price as a synthetic bold.
        value: "Archivo",
        label: { ar: "Archivo", en: "Archivo" },
        weights: "500;600;700;800;900",
    },
    {
        value: "Plus Jakarta Sans",
        label: { ar: "Plus Jakarta Sans", en: "Plus Jakarta Sans" },
        weights: "400;500;600;700;800",
    },
    {
        // Serif, and the only family here that needs its ITALIC cut: OTTO sets its
        // accent word and every dish description in italic, and a faux-oblique
        // serif is unmistakable.
        value: "Libre Caslon Text",
        label: { ar: "Libre Caslon Text", en: "Libre Caslon Text" },
        weights: "0,400;0,700;1,400",
        axis: "ital,wght",
    },
    {
        value: "IBM Plex Mono",
        label: { ar: "IBM Plex Mono", en: "IBM Plex Mono" },
        weights: "500;600",
    },
    {
        value: "Poppins",
        label: { ar: "Poppins", en: "Poppins" },
        weights: "300;400;500;600;700",
    },
    {
        value: "Rubik",
        label: { ar: "Rubik", en: "Rubik" },
        weights: "300;400;500;600;700",
    },
    {
        value: "Cairo",
        label: { ar: "Cairo", en: "Cairo" },
        // Through 900: the restaurant themes set black mastheads, and a weight the
        // stylesheet never requested is drawn by the browser as a SYNTHETIC bold —
        // visibly lighter and wider than the real cut, which is exactly how a
        // ported design stops looking like the design.
        weights: "300;400;500;600;700;800;900",
    },
    {
        value: "Readex Pro",
        label: { ar: "Readex Pro", en: "Readex Pro" },
        weights: "300;400;500;600;700",
    },
    // Families the ported product cards render in (see PRODUCT_CARD_FONTS). Listed
    // here so they are (a) selectable as the store's own typography and (b) known
    // to the storefront font loader, which derives its weight table from these
    // option lists — a family missing here would load at weight 400 only.
    {
        value: "Jost",
        label: { ar: "Jost", en: "Jost" },
        weights: "300;400;500;600;700",
    },
    {
        value: "DM Sans",
        label: { ar: "DM Sans", en: "DM Sans" },
        weights: "300;400;500;600;700",
    },
    {
        value: "Instrument Sans",
        label: { ar: "Instrument Sans", en: "Instrument Sans" },
        weights: "400;500;600;700",
    },
];
/**
 * The font each product-card style renders in.
 *
 * The nine EasyOrders ports reproduce a specific design, and the typeface is
 * part of that design — so they pin their reference family rather than
 * inheriting the store's body font. Styles absent from this map (our own four)
 * follow the theme's typography as before.
 *
 * The storefront layout feeds the ACTIVE card's family into the per-store
 * Google Fonts request, so only the family actually on screen is fetched.
 */
/**
 * Typefaces the ported SECTION variants pin, by variant value. Same contract as
 * PRODUCT_CARD_FONTS: a port that pins its reference face needs that family in
 * the stylesheet request, or it silently falls back to the theme font and stops
 * matching its source.
 *
 * Measured on the source sections. `hero-eyebrow` needs two: its banner title
 * is a serif (Newsreader) while the eyebrow and CTA stay on Jost.
 */
export const SECTION_VARIANT_FONTS = {
    // `footer-boutique` is set in Jost end to end -- 24px/200 headings over
    // 12px links. Without pinning it the port falls back to the store body face
    // and loses the thin-display contrast that defines the layout.
    "footer-boutique": ["Jost"],
    // featured-products (category-sections)
    "banner-grid": ["Instrument Sans"], // ps-featured: avyro, elite, nature, sharp
    "hero-eyebrow": ["Newsreader", "Jost"], // dark, volta
    "hero-subtitle": ["Jost"], // blast, pearl, peral, phoenix
    "hero-split": ["Jost"], // prime
    rows: ["Jost"], // hype
};
export const PRODUCT_CARD_FONTS = {
    // pearl-watches · hype-electronics · blast (grid + list) · phoenix (both)
    editorial: "Jost",
    "centered-accent": "Jost",
    clean: "Jost",
    simple: "Jost",
    bordered: "Jost",
    framed: "Jost",
    // nature (grid + related products)
    "soft-tile": "DM Sans",
    "rounded-shadow": "DM Sans",
    // elite-supplements
    card: "Instrument Sans",
    "bold-tile": "Jost",
    catalog: "Jost",
    showcase: "Instrument Sans",
};
/**
 * Options for the DISPLAY face: every Latin family plus the system-serif
 * sentinel. Derived from `LATIN_FONT_OPTIONS` so the two catalogs can't drift —
 * a family added there is offered here automatically.
 */
export const DISPLAY_FONT_OPTIONS = [
    {
        value: SYSTEM_SERIF_FONT,
        label: { ar: "سيريف النظام", en: "System serif" },
    },
    ...LATIN_FONT_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];
export const ARABIC_FONT_OPTIONS = [
    {
        value: "Cairo",
        label: { ar: "Cairo (مقترح)", en: "Cairo (recommended)" },
        // Through 900. The restaurant themes set black Arabic mastheads and prices;
        // a weight the stylesheet never requested is drawn as a SYNTHETIC bold,
        // which is visibly lighter and wider than Cairo's real 900 cut.
        weights: "300;400;500;600;700;800;900",
    },
    {
        value: "Almarai",
        label: { ar: "Almarai", en: "Almarai" },
        weights: "300;400;700;800",
    },
    {
        value: "Tajawal",
        label: { ar: "Tajawal", en: "Tajawal" },
        weights: "300;400;500;700",
    },
    {
        value: "Readex Pro",
        label: { ar: "Readex Pro", en: "Readex Pro" },
        weights: "300;400;500;600;700",
    },
    {
        value: "Noto Sans Arabic",
        label: { ar: "Noto Sans Arabic", en: "Noto Sans Arabic" },
        weights: "300;400;500;600;700",
    },
    {
        value: "IBM Plex Sans Arabic",
        label: { ar: "IBM Plex Arabic", en: "IBM Plex Arabic" },
        weights: "300;400;500;600;700",
    },
];
//# sourceMappingURL=font-catalog.js.map